import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import {
  Megaphone, Download, RefreshCw, Wand2, Plus, X, Loader2, ImageIcon,
  Search, MapPin, Globe, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { id: "beach",     label: "Beach / Tropical", swatch: "#F5B740" },
  { id: "luxury",    label: "Luxury",           swatch: "#B8945A" },
  { id: "modern",    label: "Modern",           swatch: "#0EA5E9" },
  { id: "family",    label: "Family Holiday",   swatch: "#F3A712" },
  { id: "adventure", label: "Adventure",        swatch: "#D96C29" },
];

const HOTEL_CATEGORIES = ["3 Star", "4 Star", "5 Star", "Luxury"];

// Build duration options 2N/3D → 19N/20D
const DURATIONS = Array.from({ length: 18 }, (_, i) => {
  const nights = i + 2;
  const days = nights + 1;
  return `${nights} Nights / ${days} Days`;
});

const EMPTY = {
  dest_kind: "city",
  destination: "",
  duration: "4 Nights / 5 Days",
  cost_per_person: "",
  offer_price: "",
  valid_until: "",
  hotel_category: "5 Star",
  inclusions: [],
  exclusions: [],
  important_note: "",
  template: "beach",
  show_logo_in_footer: true,
};

export default function PamphletBuilder() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const objectUrlRef = useRef("");

  const validationError = useMemo(() => {
    if (!form.destination.trim()) return "Please pick a destination";
    if (!form.duration.trim()) return "Duration is required";
    if (!form.offer_price.trim()) return "Offer price is required";
    if (!form.valid_until.trim()) return "Offer validity is required";
    if (!form.hotel_category.trim()) return "Hotel category is required";
    return "";
  }, [form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const switchKind = (kind) => {
    setForm((f) => ({ ...f, dest_kind: kind, destination: "" }));
    setImgUrl("");
  };

  const generate = async () => {
    if (validationError) { toast.error(validationError); return; }
    setBusy(true);
    try {
      const payload = {
        destination: form.destination,
        destination_kind: form.dest_kind,
        duration: form.duration,
        cost_per_person: form.cost_per_person,
        offer_price: form.offer_price,
        valid_until: form.valid_until,
        hotel_category: form.hotel_category,
        inclusions: form.inclusions,
        exclusions: form.exclusions,
        important_note: form.important_note,
        template: form.template,
        show_logo_in_footer: form.show_logo_in_footer,
      };
      const { data } = await api.post("/pamphlets/generate", payload, { responseType: "blob" });
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(data);
      objectUrlRef.current = url;
      setImgUrl(url);
      toast.success("Pamphlet generated");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Generation failed";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `${(form.destination || "pamphlet").replace(/\s+/g, "-").toLowerCase()}-pamphlet.png`;
    a.click();
  };

  const createNew = () => {
    setForm(EMPTY);
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    setImgUrl("");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar />
      <main className="flex-1">
        <header className="bg-white border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="overline text-[10px] tracking-widest text-slate-500">MARKETING</div>
              <h1 className="font-editorial text-3xl mt-1 flex items-center gap-3">
                <Megaphone size={28} className="text-emerald-600" /> Promotional Pamphlet
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Company logo & contact details are pulled from your profile automatically.
              </p>
            </div>
            {imgUrl && (
              <div className="flex items-center gap-2">
                <button data-testid="pamphlet-download-btn" onClick={download}
                        className="btn-bronze inline-flex items-center gap-2 text-sm px-4 py-2 rounded">
                  <Download size={14} /> Download image
                </button>
                <button data-testid="pamphlet-regenerate-btn" onClick={generate}
                        className="text-sm px-4 py-2 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-2">
                  <RefreshCw size={14} /> Regenerate
                </button>
                <button data-testid="pamphlet-new-btn" onClick={createNew}
                        className="text-sm px-4 py-2 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-2">
                  <Plus size={14} /> New pamphlet
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left — Form */}
            <div className="bg-white border border-border rounded p-5 space-y-5">
              {/* Destination — City / Country toggle + autocomplete */}
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">
                  Destination *
                </label>
                <div className="flex items-center gap-2 mb-2">
                  <button data-testid="dest-kind-city"
                          onClick={() => switchKind("city")}
                          className={`flex-1 py-2 rounded text-sm inline-flex items-center justify-center gap-2 border transition-colors ${
                            form.dest_kind === "city"
                              ? "bg-emerald-50 border-emerald-500 text-emerald-800"
                              : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}>
                    <MapPin size={14} /> By City
                  </button>
                  <button data-testid="dest-kind-country"
                          onClick={() => switchKind("country")}
                          className={`flex-1 py-2 rounded text-sm inline-flex items-center justify-center gap-2 border transition-colors ${
                            form.dest_kind === "country"
                              ? "bg-emerald-50 border-emerald-500 text-emerald-800"
                              : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}>
                    <Globe size={14} /> By Country
                  </button>
                </div>
                <DestinationAutocomplete
                  kind={form.dest_kind}
                  value={form.destination}
                  onChange={(v) => setField("destination", v)}
                />
                <div className="text-[11px] text-slate-400 mt-1">
                  Type at least 3 letters to see suggestions.
                </div>
              </div>

              <FieldRow label="Duration *">
                <select data-testid="pf-duration" value={form.duration}
                        onChange={(e) => setField("duration", e.target.value)}
                        className="input">
                  {DURATIONS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </FieldRow>

              <div className="grid grid-cols-2 gap-4">
                <FieldRow label="Cost / Person (optional)" hint="Shown strikethrough">
                  <input data-testid="pf-cost" value={form.cost_per_person}
                         onChange={(e) => setField("cost_per_person", e.target.value)}
                         placeholder="65000" className="input" />
                </FieldRow>
                <FieldRow label="Offer Price / Person *" hint="Biggest number on pamphlet">
                  <input data-testid="pf-offer" value={form.offer_price}
                         onChange={(e) => setField("offer_price", e.target.value)}
                         placeholder="49999" className="input" />
                </FieldRow>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FieldRow label="Offer Valid Until *">
                  <div className="relative">
                    <input data-testid="pf-valid" type="date" value={form.valid_until}
                           onChange={(e) => setField("valid_until", e.target.value)}
                           className="input pr-10" />
                    <Calendar size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </FieldRow>
                <FieldRow label="Hotel Category *">
                  <select data-testid="pf-cat" value={form.hotel_category}
                          onChange={(e) => setField("hotel_category", e.target.value)}
                          className="input">
                    {HOTEL_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </FieldRow>
              </div>

              <TagInput label="Inclusions" testid="pf-inc"
                        placeholder="Type an inclusion and press Enter"
                        items={form.inclusions}
                        onAdd={(v) => setField("inclusions", [...form.inclusions, v])}
                        onRemove={(idx) => setField("inclusions", form.inclusions.filter((_, i) => i !== idx))}
                        accent="emerald" />

              <TagInput label="Exclusions" testid="pf-exc"
                        placeholder="Type an exclusion and press Enter"
                        items={form.exclusions}
                        onAdd={(v) => setField("exclusions", [...form.exclusions, v])}
                        onRemove={(idx) => setField("exclusions", form.exclusions.filter((_, i) => i !== idx))}
                        accent="red" />

              <FieldRow label="Important Note">
                <textarea data-testid="pf-note" value={form.important_note}
                          onChange={(e) => setField("important_note", e.target.value)}
                          placeholder="Rates are subject to availability. T&C Apply."
                          rows={2} className="input" />
              </FieldRow>

              <FieldRow label="Design Template">
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {TEMPLATES.map((t) => (
                    <button key={t.id} data-testid={`pf-tpl-${t.id}`}
                            onClick={() => setField("template", t.id)}
                            className={`text-xs py-2 rounded border transition-all ${
                              form.template === t.id
                                ? "border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm"
                                : "border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}>
                      <div className="w-3 h-3 rounded-full mx-auto mb-1"
                           style={{ background: t.swatch }} />
                      {t.label}
                    </button>
                  ))}
                </div>
              </FieldRow>

              <FieldRow label="Footer Options">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    data-testid="pf-show-logo"
                    type="checkbox"
                    checked={form.show_logo_in_footer}
                    onChange={(e) => setField("show_logo_in_footer", e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  Show agency logo in footer
                </label>
                <div className="text-[11px] text-slate-500 mt-1">
                  Displays your profile logo in a circular frame on the left of the footer band.
                </div>
              </FieldRow>

              <div className="border-t border-border pt-4 space-y-2">
                <div className="text-[11px] text-slate-500 italic">
                  Travel Agent details and logo are automatically taken from your profile.
                </div>
                <button data-testid="pf-generate-btn"
                        onClick={generate} disabled={busy || !!validationError}
                        className={`w-full flex items-center justify-center gap-2 py-3 rounded font-medium transition-colors
                          ${busy || validationError
                            ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Generating…</> :
                    <><Wand2 size={16} /> Generate Pamphlet</>}
                </button>
                {validationError && (
                  <div data-testid="pf-validation" className="text-xs text-red-600">{validationError}</div>
                )}
              </div>
            </div>

            {/* Right — Preview */}
            <div className="lg:sticky lg:top-6 h-fit">
              <div className="bg-white border border-border rounded overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-slate-500">
                    Preview  ·  1080×1350  (4:5)
                  </div>
                </div>
                <div className="bg-slate-100 p-4 min-h-[500px] flex items-center justify-center">
                  {imgUrl ? (
                    <img data-testid="pamphlet-preview" src={imgUrl} alt="Pamphlet"
                         className="w-full max-w-[440px] rounded shadow-lg" />
                  ) : (
                    <div className="text-center text-slate-400">
                      <ImageIcon size={36} className="mx-auto mb-2 opacity-50" />
                      <div className="text-sm">
                        Fill the form and hit <span className="font-medium">Generate Pamphlet</span>
                      </div>
                      <div className="text-[11px] mt-1">The preview will appear here.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .input {
            width: 100%; border: 1px solid #E2E8F0; border-radius: 6px;
            padding: 10px 12px; font-size: 14px; outline: none; background: white;
          }
          .input:focus { border-color: #16A34A; box-shadow: 0 0 0 3px rgba(22,163,74,.12); }
        `}</style>
      </main>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function DestinationAutocomplete({ kind, value, onChange }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    // Close on outside click
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if ((q || "").trim().length < 3) { setResults([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/pamphlets/search", {
          params: { kind, q: q.trim() },
        });
        if (alive) { setResults(data || []); setOpen(true); }
      } catch { if (alive) setResults([]); }
    }, 180);
    return () => { alive = false; clearTimeout(t); };
  }, [q, kind]);

  const pick = (item) => {
    onChange(item.name);
    setQ(item.name);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <input
          data-testid="pf-destination"
          value={q}
          onChange={(e) => { setQ(e.target.value); onChange(e.target.value); }}
          placeholder={kind === "city" ? "Search city (e.g. Dubai)" : "Search country (e.g. Maldives)"}
          className="input pl-9"
          onFocus={() => q.length >= 3 && setOpen(true)}
        />
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
      {open && results.length > 0 && (
        <div data-testid="pf-destination-menu"
             className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg max-h-64 overflow-y-auto">
          {results.map((r) => (
            <button key={r.name}
                    data-testid={`pf-dest-option-${r.name.toLowerCase().replace(/\s+/g,'-')}`}
                    onClick={() => pick(r)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-emerald-50 border-b border-slate-100 last:border-b-0">
              <img src={r.image_url} alt="" className="w-10 h-10 object-cover rounded" />
              <div>
                <div className="text-sm text-slate-800">{r.name}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest">
                  {kind === "city" ? "City" : "Country"}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q.length >= 3 && results.length === 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg p-3 text-xs text-slate-500">
          No matches — try a different {kind}.
        </div>
      )}
    </div>
  );
}

function TagInput({ label, testid, placeholder, items, onAdd, onRemove, accent }) {
  const [v, setV] = useState("");
  const submit = () => {
    const t = v.trim();
    if (!t) return;
    onAdd(t);
    setV("");
  };
  const accentCls = accent === "red"
    ? "bg-red-50 text-red-700 border-red-200"
    : "bg-emerald-50 text-emerald-800 border-emerald-200";
  return (
    <FieldRow label={label}>
      <div className="space-y-2">
        <input
          data-testid={`${testid}-input`}
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder={placeholder}
          className="input"
        />
        {items.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid={`${testid}-tags`}>
            {items.map((t, i) => (
              <span key={i} data-testid={`${testid}-tag-${i}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${accentCls}`}>
                {t}
                <button onClick={() => onRemove(i)} data-testid={`${testid}-rm-${i}`}
                        className="opacity-60 hover:opacity-100">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="text-[11px] text-slate-400">Press Enter to add.</div>
      </div>
    </FieldRow>
  );
}
